import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { StatusMessage, InternationalizationService } from '@soff/shared-ui';
import { NotificationActions, NotificationReducer } from 'src/app/shared/state/notification';
import { TscAdminActions, TscAdminReducer } from '.';
import { TscAdminService } from '../services/admin.service';

@Injectable()
export class TscStatusHubEffects {
  private store = inject<Store<TscAdminReducer.TscAdminState>>(Store);
  private actions$ = inject(Actions);
  private apiService = inject(TscAdminService);
  private notificationStore = inject<Store<NotificationReducer.State>>(Store);
  private i18n = inject(InternationalizationService);

  getEscalationPatterns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.GET_ESCALATION_PATTERNS),
      map((action: TscAdminActions.GetEscalationPatterns) => action.payload),
      switchMap(payload =>
        this.apiService.loadEscalationPatterns(payload).pipe(
          map(response =>
            response.error
              ? new TscAdminActions.GetEscalationPatternsFailure(response.error)
              : new TscAdminActions.GetEscalationPatternsSuccess(response)
          ),
          catchError(error => of(new TscAdminActions.GetEscalationPatternsFailure(error)))
        )
      )
    )
  );

  createEscalationPattern$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.CREATE_ESCALATION_PATTERN),
      map((action: TscAdminActions.CreateEscalationPattern) => action.payload),
      mergeMap(payload =>
        this.apiService.createEscalationPattern(payload).pipe(
          map(response =>
            response.error
              ? new TscAdminActions.CreateEscalationPatternFailure(response.error, payload)
              : new TscAdminActions.CreateEscalationPatternSuccess(response, payload)
          ),
          catchError(error => of(new TscAdminActions.CreateEscalationPatternFailure(error, payload)))
        )
      )
    )
  );

  createEscalationPatternSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.CREATE_ESCALATION_PATTERN_SUCCESS),
        map((action: TscAdminActions.CreateEscalationPatternSuccess) => action?.response),
        tap(() => {
          const snackbarMessage: StatusMessage = {
            success: true,
            message: this.i18n.getTranslation('tscAdmin.escalation.snackbars.createSuccess.message', null),
          };
          this.notificationStore.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  createEscalationPatternFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.CREATE_ESCALATION_PATTERN_FAILURE),
        tap(() => {
          const snackbarMessage: StatusMessage = {
            success: false,
            message: this.i18n.getTranslation('tscAdmin.escalation.snackbars.createError.message', null),
          };
          this.notificationStore.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  updateEscalationPattern$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.UPDATE_ESCALATION_PATTERN),
      map((action: TscAdminActions.UpdateEscalationPattern) => action.payload),
      mergeMap(payload =>
        this.apiService.updateEscalationPattern(payload).pipe(
          map(response =>
            response.error
              ? new TscAdminActions.UpdateEscalationPatternFailure(response.error, payload)
              : new TscAdminActions.UpdateEscalationPatternSuccess(response, payload)
          ),
          catchError(error => of(new TscAdminActions.UpdateEscalationPatternFailure(error, payload)))
        )
      )
    )
  );

  updateEscalationPatternSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.UPDATE_ESCALATION_PATTERN_SUCCESS),
        map((action: TscAdminActions.UpdateEscalationPatternSuccess) => action?.response),
        tap(() => {
          const snackbarMessage: StatusMessage = {
            success: true,
            message: this.i18n.getTranslation('tscAdmin.escalation.snackbars.updateSuccess.message', null),
          };
          this.notificationStore.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  updateEscalationPatternFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.UPDATE_ESCALATION_PATTERN_FAILURE),
        tap(() => {
          const snackbarMessage: StatusMessage = {
            success: false,
            message: this.i18n.getTranslation('tscAdmin.escalation.snackbars.updateError.message', null),
          };
          this.notificationStore.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  getDisplayStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.GET_DISPLAY_STATUS),
      map((action: TscAdminActions.GetDisplayStatus) => action.payload),
      switchMap(payload =>
        this.apiService.loadDisplayStatus(payload).pipe(
          map(response =>
            response.error ? new TscAdminActions.GetDisplayStatusFailure(response.error) : new TscAdminActions.GetDisplayStatusSuccess(response)
          ),
          catchError(error => of(new TscAdminActions.GetDisplayStatusFailure(error)))
        )
      )
    )
  );

  createDisplayStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.CREATE_DISPLAY_STATUS),
      map((action: TscAdminActions.CreateDisplayStatus) => action.payload),
      mergeMap(payload =>
        this.apiService.createDisplayStatus(payload).pipe(
          map(response =>
            response.error
              ? new TscAdminActions.CreateDisplayStatusFailure(response.error, payload)
              : new TscAdminActions.CreateDisplayStatusSuccess(response, payload)
          ),
          catchError(error => of(new TscAdminActions.CreateDisplayStatusFailure(error, payload)))
        )
      )
    )
  );

  createDisplayStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.CREATE_DISPLAY_STATUS_SUCCESS),
        map((action: TscAdminActions.CreateDisplayStatusSuccess) => action?.response),
        tap(() => {
          const snackbarMessage: StatusMessage = {
            success: true,
            message: this.i18n.getTranslation('tscAdmin.displayStatus.snackbars.createSuccess.message', null),
          };
          this.notificationStore.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  createDisplayStatusFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.CREATE_DISPLAY_STATUS_FAILURE),
        tap(() => {
          const snackbarMessage: StatusMessage = {
            success: false,
            message: this.i18n.getTranslation('tscAdmin.displayStatus.snackbars.createError.message', null),
          };
          this.notificationStore.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  updateDisplayStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.UPDATE_DISPLAY_STATUS),
      map((action: TscAdminActions.UpdateDisplayStatus) => action.payload),
      mergeMap(payload =>
        this.apiService.updateDisplayStatus(payload).pipe(
          map(response =>
            response.error
              ? new TscAdminActions.UpdateDisplayStatusFailure(response.error, payload)
              : new TscAdminActions.UpdateDisplayStatusSuccess(response, payload)
          ),
          catchError(error => of(new TscAdminActions.UpdateDisplayStatusFailure(error, payload)))
        )
      )
    )
  );

  updateDisplayStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.UPDATE_DISPLAY_STATUS_SUCCESS),
        map((action: TscAdminActions.UpdateDisplayStatusSuccess) => action?.response),
        tap(() => {
          const snackbarMessage: StatusMessage = {
            success: true,
            message: this.i18n.getTranslation('tscAdmin.displayStatus.snackbars.updateSuccess.message', null),
          };
          this.notificationStore.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  updateDisplayStatusFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.UPDATE_DISPLAY_STATUS_FAILURE),
        tap(() => {
          const snackbarMessage: StatusMessage = {
            success: false,
            message: this.i18n.getTranslation('tscAdmin.displayStatus.snackbars.updateError.message', null),
          };
          this.notificationStore.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );
}
